import { Clear, Image as ImageIcon } from '@mui/icons-material';
import { IconButton, FormHelperText } from '@mui/material';
import { isString, useField } from 'formik';
import React, { useCallback, useState } from 'react';

import { FormikTextField, UploadQRDialog } from 'components';
import { FormFieldProps, GetFieldValidatorFn } from 'types';
import { formatUtils } from 'utils';

import { getTCommon, useTFeature } from '../fields.utils';
import { getBaseFieldValidator } from '../form.validators';

export const Field: React.FC<FormFieldProps> = ({
  name,
  required = false,
  disabled = false,
}) => {
  const { t } = useTFeature();
  const [open, setOpen] = useState(false);

  const [field, meta, helpers] = useField(name);
  const [isFocused, setIsFocused] = useState(false);

  const isFormatInvalid =
    !!field.value &&
    !isFocused &&
    !formatUtils.isValidAccountNumberFormat(field.value);
  const shouldDisableField = disabled || isFormatInvalid;

  const handleClose = useCallback(
    (value?: string) => {
      if (isString(value)) {
        helpers.setValue(value);
      }
      setOpen(false);
    },
    [helpers],
  );

  const handleClear = useCallback(() => {
    helpers.setValue('');
  }, [helpers]);

  return (
    <>
      <div className="tw-flex tw-items-center">
        <FormikTextField
          name={name}
          label={t('fields.account_number.label')}
          fullWidth
          required={required}
          disabled={shouldDisableField}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        {isFormatInvalid && (
          <IconButton sx={{ ml: 1 }} onClick={handleClear}>
            <Clear />
          </IconButton>
        )}
        <IconButton
          sx={{ ml: 1 }}
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          <ImageIcon />
        </IconButton>
        <UploadQRDialog open={open} onClose={handleClose} />
      </div>
      {meta.error && field.value && (
        <FormHelperText error>{meta.error}</FormHelperText>
      )}
    </>
  );
};

export const getValidator: GetFieldValidatorFn = ({ required }) => {
  const tCommon = getTCommon();

  return getBaseFieldValidator({ required }).test(
    'not-url',
    tCommon('errors.no_url_allowed'),
    (value) => {
      if (!value) {
        return true;
      }
      try {
        new URL(value);
        return false;
      } catch {
        return true;
      }
    },
  );
};
